import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

const PLAN_HIERARCHY = { professional: 1, business: 2, starter: 1, pro: 1, team: 2, agency: 2 };

const PLAN_DETAILS = {
  professional: { label: 'Professional', price: '$399/mo',  color: 'var(--accent)', badgeBg: 'var(--blue-bg)',  badgeBorder: 'rgba(45,125,210,0.3)'   },
  business:     { label: 'Business',     price: '$799/mo',  color: 'var(--green)',  badgeBg: 'var(--green-bg)', badgeBorder: 'rgba(29,184,122,0.3)'   },
};

// Feature groups with the minimum plan that unlocks each feature
const FEATURE_GROUPS = [
  {
    label: 'Professional Features',
    features: [
      { label: 'Compliance Dashboard & Readiness Score',    plan: 'professional' },
      { label: 'Self-Assessment Questionnaire',             plan: 'professional' },
      { label: 'Gap Analysis Dashboard',                    plan: 'professional' },
      { label: 'Remediation Task Tracker',                  plan: 'professional' },
      { label: 'Evidence Locker',                           plan: 'professional' },
      { label: 'Company Profile Management',                plan: 'professional' },
      { label: 'CMMC Level 1, 2 & 3 Assessment',           plan: 'professional' },
      { label: 'NIST SP 800-171 Assessment',                plan: 'professional' },
      { label: 'SSP Document Reader & AI Import',           plan: 'professional' },
      { label: 'AI Compliance Assistant (50/mo)',           plan: 'professional' },
      { label: 'Policy Template Library (7 templates)',     plan: 'professional' },
      { label: 'Auditor Access for C3PAOs',                 plan: 'professional' },
      { label: 'Annual Affirmation Tracker',                plan: 'professional' },
      { label: 'Reports & SSP PDF Export',                  plan: 'professional' },
      { label: 'Up to 3 team members',                      plan: 'professional' },
    ],
  },
  {
    label: 'Business Features',
    features: [
      { label: 'SOC 2 Type II Assessment (87 controls)',   plan: 'business' },
      { label: 'ISO 27001:2022 Assessment (93 controls)',  plan: 'business' },
      { label: 'HIPAA Security Rule Assessment',           plan: 'business' },
      { label: 'NIST CSF 2.0 Assessment',                  plan: 'business' },
      { label: 'PCI DSS v4.0 & FedRAMP Moderate',         plan: 'business' },
      { label: 'AI Compliance Assistant (200/mo)',         plan: 'business' },
      { label: 'Unlimited team seats',                     plan: 'business' },
      { label: 'Dedicated account manager',                plan: 'business' },
      { label: 'Phone & priority email support',           plan: 'business' },
    ],
  },
];

// Next upgrade plan for a given plan key
const NEXT_PLAN = { professional: 'business' };

export default function Billing({ session, currentPlan = 'professional', onUpgrade, onDPA }) {
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError,   setPortalError]   = useState('');
  const [planLoading,   setPlanLoading]   = useState(true);
  const [localPlan,     setLocalPlan]     = useState(currentPlan || 'professional');
  const [planStatus,    setPlanStatus]    = useState('');
  const [isTrial,       setIsTrial]       = useState(false);
  const [memberSince,   setMemberSince]   = useState('');

  // Always fetch the live plan from the database — never rely solely on prop
  useEffect(() => {
    const fetchPlan = async () => {
      if (!session?.user?.id) { setPlanLoading(false); return; }
      const { data } = await supabase
        .from('profiles')
        .select('plan, plan_status, is_trial, created_at')
        .eq('id', session.user.id)
        .single();
      if (data) {
        // Normalize legacy plan names to canonical keys
        if (data.plan) {
          const p = data.plan.toLowerCase();
          if (p === 'business' || p === 'team' || p === 'agency') setLocalPlan('business');
          else setLocalPlan('professional');
        }
        if (data.plan_status) setPlanStatus(data.plan_status);
        if (data.is_trial !== undefined && data.is_trial !== null) setIsTrial(!!data.is_trial);
        if (data.created_at) {
          setMemberSince(new Date(data.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
        }
      }
      setPlanLoading(false);
    };
    fetchPlan();
  }, [session]);

  const planKey    = localPlan.toLowerCase();
  const planInfo   = PLAN_DETAILS[planKey] || PLAN_DETAILS.professional;
  const planLevel  = PLAN_HIERARCHY[planKey] ?? 1;
  const nextPlanId = NEXT_PLAN[planKey];
  const nextPlan   = nextPlanId ? PLAN_DETAILS[nextPlanId] : null;

  // Features locked out on this plan
  const lockedFeatures = FEATURE_GROUPS
    .flatMap(g => g.features)
    .filter(f => (PLAN_HIERARCHY[f.plan] || 1) > planLevel);

  // Visibility rules for Manage Subscription button
  const isActivePaid = planStatus === 'active' && !!localPlan && !isTrial;
  const hasPlan      = !!localPlan && planStatus === 'active';

  async function handleManageSubscription() {
    setPortalLoading(true);
    setPortalError('');
    try {
      const response = await supabase.functions.invoke('create-portal-session', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (response.error) throw new Error(response.error.message);
      if (!response.data?.url) throw new Error('No portal URL returned');

      window.location.href = response.data.url;
    } catch (err) {
      console.error('Portal error:', err);
      setPortalError(err.message || 'Failed to open subscription management. Please try again.');
      setPortalLoading(false);
    }
  }

  return (
    <div style={{ padding: 28, overflowY: 'auto', height: '100%' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
          Billing and Subscription
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
          Manage your plan, payment method, and billing history
        </div>
      </div>

      {/* ── Trial card ── */}
      {!planLoading && isTrial && (
        <div style={{
          background: 'var(--amber-bg)', border: '1px solid rgba(245,166,35,0.4)',
          borderRadius: 10, padding: 20, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, fontWeight: 600 }}>
                Free Trial
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                TheClearAudit Trial
              </div>
            </div>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
              background: 'rgba(245,166,35,0.2)', color: 'var(--amber)',
              border: '1px solid rgba(245,166,35,0.5)',
              textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--mono)',
            }}>Trial</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 16 }}>
            You're on a free trial. Upgrade to a paid plan to unlock all features and manage your subscription.
          </div>
          <button
            onClick={onUpgrade}
            style={{
              width: '100%', padding: '10px', borderRadius: 6,
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              border: '1px solid var(--accent)',
              background: 'var(--accent)', color: '#fff',
              fontFamily: 'var(--font)',
            }}
          >
            Upgrade to a Paid Plan
          </button>
        </div>
      )}

      {/* ── No subscription card ── */}
      {!planLoading && !isTrial && !hasPlan && (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 20, marginBottom: 16,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            No active subscription
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16, lineHeight: 1.6 }}>
            Choose a plan to get started with TheClearAudit.
          </div>
          <button
            onClick={onUpgrade}
            style={{
              padding: '9px 24px', borderRadius: 6,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: '1px solid var(--accent)',
              background: 'var(--accent)', color: '#fff',
              fontFamily: 'var(--font)',
            }}
          >
            Choose a Plan
          </button>
        </div>
      )}

      {/* ── Current Plan Card (active paid subscribers) ── */}
      {!planLoading && (isTrial || hasPlan) && (
        <div style={{
          background: 'var(--bg2)', border: `1px solid ${planInfo.color}40`,
          borderRadius: 10, padding: 20, marginBottom: 16,
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start', marginBottom: 16,
            paddingBottom: 14, borderBottom: '1px solid var(--border)',
          }}>
            <div>
              <div style={{
                fontSize: 11, color: 'var(--text3)', marginBottom: 6,
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>
                Current Plan
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: planInfo.color }}>
                  {planInfo.label}
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                  background: planInfo.badgeBg, color: planInfo.color,
                  border: `1px solid ${planInfo.badgeBorder}`,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  fontFamily: 'var(--mono)',
                }}>
                  {planInfo.label}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {isTrial ? (
                  <span style={{
                    display: 'inline-block', fontSize: 10, fontWeight: 600,
                    padding: '2px 8px', borderRadius: 10,
                    background: 'var(--amber-bg)', color: 'var(--amber)',
                    border: '1px solid rgba(245,166,35,0.4)',
                  }}>Trial</span>
                ) : planStatus === 'active' ? (
                  <span style={{
                    display: 'inline-block', fontSize: 10, fontWeight: 600,
                    padding: '2px 8px', borderRadius: 10,
                    background: 'var(--green-bg)', color: 'var(--green)',
                    border: '1px solid rgba(29,184,122,0.4)',
                  }}>Active</span>
                ) : planStatus === 'canceled' ? (
                  <span style={{
                    display: 'inline-block', fontSize: 10, fontWeight: 600,
                    padding: '2px 8px', borderRadius: 10,
                    background: 'var(--red-bg)', color: 'var(--red)',
                    border: '1px solid rgba(224,82,82,0.4)',
                  }}>Cancelled</span>
                ) : null}
                {memberSince && (
                  <span style={{ fontSize: 10, color: 'var(--text3)' }}>
                    Member since {memberSince}
                  </span>
                )}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: planInfo.color }}>
                {planInfo.price}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                billed monthly
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                Next billing: view in portal
              </div>
            </div>
          </div>

          {portalError && (
            <div style={{
              background: 'var(--red-bg)', border: '1px solid var(--red)',
              borderRadius: 6, padding: '10px 12px', fontSize: 12,
              color: 'var(--red)', marginBottom: 14,
            }}>{portalError}</div>
          )}

          {/* Manage Subscription button — only for active paid subscribers */}
          {isActivePaid ? (
            <>
              <button
                onClick={handleManageSubscription}
                disabled={portalLoading}
                style={{
                  width: '100%', padding: '10px', borderRadius: 6,
                  fontSize: 13, fontWeight: 600,
                  cursor: portalLoading ? 'not-allowed' : 'pointer',
                  border: `1px solid ${planInfo.color}`,
                  background: planInfo.color, color: '#fff',
                  fontFamily: 'var(--font)',
                  opacity: portalLoading ? 0.7 : 1,
                  transition: 'opacity 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {!portalLoading && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                )}
                {portalLoading ? 'Opening...' : 'Manage Subscription'}
              </button>
              <div style={{
                fontSize: 11, color: 'var(--text3)',
                textAlign: 'center', marginTop: 6,
              }}>
                Update payment method, view invoices, or cancel your subscription
              </div>
            </>
          ) : isTrial ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
                Upgrade to a paid plan to manage your subscription
              </div>
              <button
                onClick={onUpgrade}
                style={{
                  padding: '9px 20px', borderRadius: 6,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: '1px solid var(--accent)',
                  background: 'var(--accent)', color: '#fff',
                  fontFamily: 'var(--font)',
                }}
              >
                Upgrade Now
              </button>
            </div>
          ) : null}
        </div>
      )}

      {/* Skeleton loader while fetching plan */}
      {planLoading && (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 20, marginBottom: 16,
        }}>
          <div className="skeleton" style={{ height: 24, width: 120, borderRadius: 6, marginBottom: 12 }} />
          <div className="skeleton" style={{ height: 40, width: '100%', borderRadius: 6 }} />
        </div>
      )}

      {/* What's included */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: 'var(--text)',
          marginBottom: 16, paddingBottom: 12,
          borderBottom: '1px solid var(--border)',
        }}>
          What's included in your plan
        </div>

        {FEATURE_GROUPS.map(group => {
          const groupLevel    = PLAN_HIERARCHY[group.features[0]?.plan] || 1;
          const groupIncluded = groupLevel <= planLevel;

          return (
            <div key={group.label} style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 10, fontWeight: 600, color: 'var(--text3)',
                textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{
                  display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                  background: groupIncluded ? 'var(--green)' : 'var(--border2)',
                  flexShrink: 0,
                }} />
                {group.label}
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: '5px 12px',
              }}>
                {group.features.map(f => {
                  const featureLevel    = PLAN_HIERARCHY[f.plan] || 1;
                  const featureIncluded = featureLevel <= planLevel;
                  const unlockPlan      = featureIncluded ? null : PLAN_DETAILS[f.plan];
                  return (
                    <div key={f.label} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 7,
                      fontSize: 12,
                      color: featureIncluded ? 'var(--text2)' : 'var(--text3)',
                      opacity: featureIncluded ? 1 : 0.65,
                    }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                        background: featureIncluded ? 'var(--green-bg)' : 'var(--surface2)',
                        border: `1px solid ${featureIncluded ? 'var(--green)' : 'var(--border)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 8, fontWeight: 700,
                        color: featureIncluded ? 'var(--green)' : 'var(--text3)',
                      }}>
                        {featureIncluded ? '✓' : '✕'}
                      </div>
                      <span style={{ lineHeight: 1.4 }}>
                        {f.label}
                        {unlockPlan && (
                          <span style={{
                            marginLeft: 4, fontSize: 9, fontWeight: 600,
                            color: unlockPlan.color, fontFamily: 'var(--mono)',
                          }}>
                            [{unlockPlan.label}]
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Upgrade card — shown when locked features exist */}
      {lockedFeatures.length > 0 && nextPlan && (
        <div style={{
          background: `linear-gradient(135deg, ${nextPlan.color}0D, ${nextPlan.color}05)`,
          border: `1px solid ${nextPlan.color}35`,
          borderRadius: 10, padding: 20, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                Unlock {lockedFeatures.length} more features
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                Upgrade to <span style={{ color: nextPlan.color, fontWeight: 600 }}>{nextPlan.label}</span> to access everything below
              </div>
            </div>
            <div style={{
              padding: '4px 12px', borderRadius: 20, flexShrink: 0,
              background: `${nextPlan.color}15`, border: `1px solid ${nextPlan.color}40`,
              fontSize: 13, fontWeight: 700, color: nextPlan.color,
            }}>
              {nextPlan.price}
            </div>
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: '6px 12px', marginBottom: 18,
          }}>
            {lockedFeatures.slice(0, 8).map(f => (
              <div key={f.label} style={{
                display: 'flex', alignItems: 'flex-start', gap: 7,
                fontSize: 11, color: 'var(--text2)',
              }}>
                <span style={{ color: nextPlan.color, flexShrink: 0, marginTop: 2, fontWeight: 700 }}>+</span>
                <span>{f.label}</span>
              </div>
            ))}
            {lockedFeatures.length > 8 && (
              <div style={{ fontSize: 11, color: 'var(--text3)', paddingLeft: 14 }}>
                + {lockedFeatures.length - 8} more features...
              </div>
            )}
          </div>
          <button
            onClick={onUpgrade}
            style={{
              width: '100%', padding: '11px', borderRadius: 7,
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              border: `1px solid ${nextPlan.color}`,
              background: nextPlan.color, color: '#fff',
              fontFamily: 'var(--font)', transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="18 15 12 9 6 15"/>
            </svg>
            Upgrade to {nextPlan.label} — {nextPlan.price}
          </button>
          <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', marginTop: 8 }}>
            Cancel anytime · 7-day money back guarantee
          </div>
        </div>
      )}

      {/* Billing Information */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: 'var(--text)',
          marginBottom: 14, paddingBottom: 10,
          borderBottom: '1px solid var(--border)',
        }}>
          Billing Information
        </div>
        {[
          { label: 'Account Email',  value: session?.user?.email || 'Not set' },
          { label: 'Billing Cycle',  value: 'Monthly'                         },
          { label: 'Payment Method', value: 'Manage in billing portal'        },
          { label: 'Next Invoice',   value: 'View in billing portal'          },
        ].map(item => (
          <div key={item.label} style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', padding: '9px 0',
            borderBottom: '1px solid var(--border)', fontSize: 12,
          }}>
            <span style={{ color: 'var(--text3)' }}>{item.label}</span>
            <span style={{ fontWeight: 500, color: 'var(--text)' }}>{item.value}</span>
          </div>
        ))}
      </div>

      {/* Enterprise / DPA */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
            Enterprise customers
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>
            Need a signed Data Processing Agreement (DPA) for your vendor compliance program?
          </div>
        </div>
        <button
          onClick={onDPA || (() => window.open('mailto:theclearaudit@gmail.com?subject=DPA%20Request%20%E2%80%94%20%5BCompany%20Name%5D', '_blank'))}
          style={{
            flexShrink: 0, fontSize: 12, fontWeight: 600, padding: '7px 16px', borderRadius: 6,
            cursor: 'pointer', border: '1px solid var(--accent)',
            background: 'transparent', color: 'var(--accent)',
            fontFamily: 'var(--font)', transition: 'all 0.15s', whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--blue-bg)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          Request Signed DPA →
        </button>
      </div>

      {/* Cancel — only show for active paid subscribers */}
      {isActivePaid && (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 20,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            Need to cancel?
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 14 }}>
            You can cancel your subscription at any time from the billing portal.
            Your access will continue until the end of your current billing period.
            No refunds are issued for partial months.
          </div>
          <button
            onClick={handleManageSubscription}
            disabled={portalLoading}
            style={{
              fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--red)',
              background: 'transparent', color: 'var(--red)',
              fontFamily: 'var(--font)', transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--red)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--red)'; }}
          >
            Cancel Subscription
          </button>
        </div>
      )}

    </div>
  );
}
